import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Button, Spin, message, Statistic, Space, Tooltip, Select, Popconfirm, Modal, Form, InputNumber, Checkbox, Input, Switch } from 'antd';
import { themeConfig } from '../config/theme';
import { ReloadOutlined, SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, CalendarOutlined, UserOutlined, FileTextOutlined, ClockCircleOutlined, EyeOutlined, DollarOutlined, PrinterOutlined, DownloadOutlined, LineChartOutlined } from '@ant-design/icons';
import { listUsers } from '../services/userService';
import { getSalaryPaymentData, addPartialPayment, editPartialPayment, deletePartialPayment } from '../services/salaryPaymentService';
import { renderActivityDot } from '../utils/activityDot';
import dayjs from 'dayjs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';

const { Title } = Typography;
const { Search } = Input;

const SalaryPayment = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedMonth, setSelectedMonth] = useState(dayjs().subtract(1, 'month').startOf('month'));
  const [paymentData, setPaymentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState(null);
  
  // Partial payment states
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentNarration, setPaymentNarration] = useState('');
  const [partialPaymentsModalVisible, setPartialPaymentsModalVisible] = useState(false);
  const [selectedEmployeePartialPayments, setSelectedEmployeePartialPayments] = useState([]);
  const [isPrintModalVisible, setIsPrintModalVisible] = useState(false);
  
  // PDF Salary Slip Modal states
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [selectedEmployeeForPdf, setSelectedEmployeeForPdf] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');

  // Edit/Delete Partial Payment states
  const [editPaymentModalVisible, setEditPaymentModalVisible] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentAmount, setEditPaymentAmount] = useState(0);
  const [editPaymentNarration, setEditPaymentNarration] = useState('');

  // Graph modal states
  const [graphModalVisible, setGraphModalVisible] = useState(false);
  const [graphData, setGraphData] = useState([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [showGrossSalary, setShowGrossSalary] = useState(false);

  useEffect(() => {
    fetchPaymentData();
  }, [selectedMonth]);

  const fetchPaymentData = async () => {
    setLoading(true);
    try {
      const monthYear = selectedMonth.format('YYYY-MM');
      const data = await getSalaryPaymentData(monthYear);
      console.log('DEBUG: Payment Data: ', data);
      setPaymentData(data.paymentData || []);
    } catch (error) {
      messageApi.error('Failed to fetch payment data');
    } finally {
      setLoading(false);
    }
  };

  const handlePartialPayment = (employee) => {
    setSelectedEmployee(employee);
    // Set the remaining balance as the default payment amount
    const remainingBalance = (employee.netPayable || 0) - (employee.partialPaidAmount || 0);
    setPaymentAmount(remainingBalance);
    setPaymentModalVisible(true);
  };

  const handlePaymentSubmit = async () => {
    if (!selectedEmployee || paymentAmount <= 0) {
      messageApi.warning('Please enter a valid payment amount');
      return;
    }

    const remainingBalance = (selectedEmployee.netPayable || 0) - (selectedEmployee.partialPaidAmount || 0);
    if (paymentAmount > remainingBalance) {
      messageApi.warning('Payment amount cannot exceed remaining balance');
      return;
    }

    setPaymentLoading(true);
    try {
      const monthYear = selectedMonth.format('YYYY-MM');
      await addPartialPayment(
        selectedEmployee.mobileNumber,
        monthYear,
        paymentAmount,
        selectedEmployee.netPayable,
        paymentNarration
      );
      
      messageApi.success('Payment recorded successfully');
      setPaymentModalVisible(false);
      setSelectedEmployee(null);
      setPaymentAmount(0);
      setPaymentNarration('');
      fetchPaymentData(); // Refresh data
    } catch (error) {
      if (error.response && error.response.status === 404) {
        messageApi.error('Partial payment feature not yet deployed. Please contact administrator.');
      } else {
        messageApi.error('Failed to record payment');
      }
      console.error('Payment error:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const showPartialPayments = (employee) => {
    setSelectedEmployee(employee); // Set the selected employee
    setSelectedEmployeePartialPayments(employee.partialPayments || []);
    setPartialPaymentsModalVisible(true);
  };

  const handleEditPayment = (payment, employee, index) => {
    const employeeToUse = employee || selectedEmployee; // Use passed employee or selectedEmployee
    setEditingPayment({ ...payment, employee: employeeToUse, paymentIndex: index });
    setEditPaymentAmount(payment.amount);
    setEditPaymentNarration(payment.narration || '');
    setEditPaymentModalVisible(true);
  };

  const handleEditPaymentSubmit = async () => {
    if (!editingPayment || editPaymentAmount <= 0) {
      messageApi.error('Please enter a valid amount');
      return;
    }

    if (!editingPayment.employee || !editingPayment.employee.mobileNumber) {
      messageApi.error('Employee information not available');
      return;
    }

    try {
      const monthYear = selectedMonth.format('YYYY-MM');
      await editPartialPayment(
        editingPayment.employee.mobileNumber,
        monthYear,
        editingPayment.paymentIndex,
        editPaymentAmount,
        editPaymentNarration
      );

      messageApi.success('Payment updated successfully');
      setEditPaymentModalVisible(false);
      setPartialPaymentsModalVisible(false); // Close the partial payments modal
      setEditingPayment(null);
      setEditPaymentAmount(0);
      setEditPaymentNarration('');
      fetchPaymentData(); // Refresh data
    } catch (error) {
      if (error.response && error.response.status === 404) {
        messageApi.error('Payment feature not yet deployed. Please contact administrator.');
      } else {
        messageApi.error('Failed to update payment');
      }
      console.error('Edit payment error:', error);
    }
  };

  const handleDeletePayment = async (payment, employee, index) => {
    const employeeToUse = employee || selectedEmployee; // Use passed employee or selectedEmployee
    
    if (!employeeToUse || !employeeToUse.mobileNumber) {
      messageApi.error('Employee information not available');
      return;
    }
    
    try {
      const monthYear = selectedMonth.format('YYYY-MM');
      await deletePartialPayment(
        employeeToUse.mobileNumber,
        monthYear,
        index
      );

      messageApi.success('Payment deleted successfully');
      setPartialPaymentsModalVisible(false); // Close the partial payments modal
      fetchPaymentData(); // Refresh data
      // Update the partial payments modal data
      const updatedPayments = selectedEmployeePartialPayments.filter((_, paymentIndex) => paymentIndex !== index);
      setSelectedEmployeePartialPayments(updatedPayments);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        messageApi.error('Payment feature not yet deployed. Please contact administrator.');
      } else {
        messageApi.error('Failed to delete payment');
      }
      console.error('Delete payment error:', error);
    }
  };

  const handlePrint = () => {
    if (!paymentData || paymentData.length === 0) {
      messageApi.warn('Please load payment data first.');
      return;
    }
    setIsPrintModalVisible(true);
  };

  const handlePrintContent = () => {
    const printContent = document.getElementById('printable-salary-payment-content');
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    
    // Re-render the component after printing
    window.location.reload();
  };

  const handleViewSalarySlip = (employee) => {
    if (!employee.salarySlipGenerated) {
      messageApi.warning('Salary slip not generated for this employee');
      return;
    }
    
    if (!employee.pdfUrl) {
      messageApi.warning('PDF not available for this salary slip');
      return;
    }
    
    setSelectedEmployeeForPdf(employee);
    setPdfUrl(employee.pdfUrl);
    setPdfModalVisible(true);
  };

  const handleShowGraph = async () => {
    setGraphModalVisible(true);
    setGraphLoading(true);
    
    try {
      const chartData = [];
      
      // Fetch data for past 6 months (including current month minus 1)
      for (let i = 5; i >= 0; i--) {
        const month = dayjs().subtract(i, 'month').startOf('month');
        const monthYear = month.format('YYYY-MM');
        
        try {
          const response = await getSalaryPaymentData(monthYear);
          const data = response.paymentData || [];
          
          // Calculate total salary for this month
          const dataWithSlips = data.filter(item => item.salarySlipGenerated);
          const totalSalary = dataWithSlips.reduce((sum, item) => sum + (item.netPayable || 0), 0);
          
          console.log(`Graph data for ${monthYear}:`, { 
            totalEmployees: data.length,
            withSlips: dataWithSlips.length,
            totalSalary 
          });
          
          chartData.push({
            month: month.format('MMM YYYY'),
            totalSalary: totalSalary,
            employees: dataWithSlips.length
          });
        } catch (error) {
          console.error(`Error fetching data for ${monthYear}:`, error);
          // Add zero data for months with errors
          chartData.push({
            month: month.format('MMM YYYY'),
            totalSalary: 0,
            employees: 0
          });
        }
      }
      
      console.log('Final chart data:', chartData);
      setGraphData(chartData);
    } catch (error) {
      messageApi.error('Failed to fetch graph data');
      console.error('Graph data error:', error);
    } finally {
      setGraphLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (!filteredData || filteredData.length === 0) return;

    const getPaymentStatusLabel = (status) => {
      switch (status) {
        case 'PAID': return 'Fully Paid';
        case 'PENDING': return 'Pending';
        case 'PARTIAL_PAID': return 'Partial Paid';
        case 'CARRIED_FORWARD': return 'Carried Forward';
        case 'AUTO': return 'Auto Generated';
        case 'ADVANCE': return 'Adjusted Against Advance';
        default: return status || 'N/A';
      }
    };

    const headers = [
      'Employee Name',
      'Payment Account',
      'Salary Slip Status',
      'Payment Status',
      'Net Payable',
      'Partial Paid Amount'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredData.map(record => [
        `"${record.fullName || ''} (${record.job || 'N/A'})"`,
        `"${record.paymentAccount || 'Not Set'}"`,
        `"${record.salarySlipGenerated ? 'Generated' : 'Not Generated'}"`,
        `"${record.salarySlipGenerated ? getPaymentStatusLabel(record.paymentStatus) : 'N/A'}"`,
        `"${record.salarySlipGenerated ? (record.netPayable || 0) : 'N/A'}"`,
        `"${record.salarySlipGenerated && record.partialPaidAmount ? record.partialPaidAmount : 'N/A'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `salary_payment_${selectedMonth.format('MMMM_YYYY')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFilteredData = () => {
    let filtered = paymentData.filter(item => 
      item.salarySlipGenerated);
    
    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item => 
        item.fullName?.toLowerCase().includes(searchLower) ||
        item.job?.toLowerCase().includes(searchLower) ||
        item.mobileNumber?.includes(searchText)
      );
    }
    
    // Apply payment account filter
    if (selectedPaymentAccount) {
      filtered = filtered.filter(item => 
        item.paymentAccount === selectedPaymentAccount
      );
    }
    
    // Apply pending only filter - show records where remaining balance (Net Payable - Partial Paid) > 0
    if (showPendingOnly) {
      filtered = filtered.filter(item => {
        if (!item.salarySlipGenerated) return false;
        const netPayable = item.netPayable || 0;
        const partialPaidAmount = item.partialPaidAmount || 0;
        const remainingBalance = netPayable - partialPaidAmount;
        return remainingBalance > 0;
      });
    }
    
    return filtered;
  };

  const getStatistics = () => {
    // Use unfiltered data for statistics - only filter for salary slip generated
    const dataWithSlips = paymentData.filter(item => item.salarySlipGenerated);
    const totalUsers = paymentData.length;
    const generatedSlips = dataWithSlips.length;
    const salarySlipPending = totalUsers - generatedSlips;
    
    // Payment pending: users with no full or partial payments (paymentStatus === 'PENDING')
    const pendingPayments = dataWithSlips.filter(item => 
      item.paymentStatus === 'PENDING'
    ).length;
    const partialPayments = dataWithSlips.filter(item => 
      item.paymentStatus === 'PARTIAL_PAID'
    ).length;
    const paidSlips = dataWithSlips.filter(item => 
      item.paymentStatus === 'PAID'
    ).length;
    
    // Payment carried forward: users with paymentStatus === 'CARRIED_FORWARD'
    const carriedForward = dataWithSlips.filter(item => 
      item.paymentStatus === 'CARRIED_FORWARD'
    ).length;
    
    // Payment pending - sum of Net Payable from table
    const totalSalary = dataWithSlips
      .reduce((sum, item) => sum + (item.netPayable || 0), 0);
    
    // Gross salary - sum of netPayable + advanceDeducted
    const totalGrossSalary = dataWithSlips
      .reduce((sum, item) => {
        const netPayable = item.netPayable || 0;
        const advanceDeducted = item.advanceDeducted || 0;
        return sum + (netPayable + advanceDeducted);
      }, 0);
    
    // Total paid - sum of all partial paid amounts
    const totalPaid = dataWithSlips
      .reduce((sum, item) => {
        const partialPaid = item.partialPaidAmount || 0;
        return sum + partialPaid;
      }, 0);

    // Total salary - sum of pending payment and total paid
    const paymentPending = totalSalary - totalPaid;

    return {
      totalUsers,
      generatedSlips,
      salarySlipPending,
      pendingPayments,
      partialPayments,
      paidSlips,
      carriedForward,
      paymentPending,
      totalPaid,
      totalSalary,
      totalGrossSalary
    };
  };

  const stats = getStatistics();
  const filteredData = getFilteredData();

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text, record) => {
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              {renderActivityDot(record.activeStatus)}
              <span style={{ fontWeight: '500' }}>
                {record.fullName}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: themeConfig.text.muted, marginBottom: '2px' }}>
            {record.job || 'N/A'} | {record.employmentType || 'N/A'}
            </div>
            <div style={{ fontSize: '12px', color: themeConfig.text.muted }}>
              <strong>Account:</strong> {record.paymentAccount || <span style={{ color: themeConfig.text.mutedVeryLight }}>Not Set</span>}
            </div>
          </div>
        );
      },
      width: 250,
    },
    {
      title: 'Salary Slip Status',
      dataIndex: 'salarySlipGenerated',
      key: 'salarySlipGenerated',
      render: (generated) => (
        <Tag color={generated ? 'green' : 'orange'} icon={generated ? <EyeOutlined /> : null}>
          {generated ? 'Generated' : 'Not Generated'}
        </Tag>
      ),
    },
    {
      title: 'Payment Status',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status, record) => {
        if (!record.salarySlipGenerated) {
          return <Tag color="default">N/A</Tag>;
        }
        
        if (status === 'PARTIAL_PAID') {
          return (
            <Tag color="orange" icon={<EyeOutlined />}>
              Partial Paid
            </Tag>
          );
        }
        
        if (status === 'CARRIED_FORWARD') {
          return (
            <Tag color="default" icon={<EyeOutlined />}>
              Carried Forward
            </Tag>
          );
        }
        
        if (status === 'PAID') {
          return (
            <Tag color="green" icon={<EyeOutlined />}>
              Fully Paid
            </Tag>
          );
        }
        
        if (status === 'AUTO') {
          return (
            <Tag color="blue" icon={<EyeOutlined />}>
              Auto Generated
            </Tag>
          );
        }
        
        if (status === 'ADVANCE') {
          return (
            <Tag color="green" icon={<EyeOutlined />}>
              Adjusted Against Advance
            </Tag>
          );
        }
        
        return (
          <Tag color="red">
            Pending
          </Tag>
        );
      },
    },
    {
      title: 'Net Payable',
      dataIndex: 'netPayable',
      key: 'netPayable',
      render: (value, record) => {
        if (!record.salarySlipGenerated) {
          return (
            <Tooltip title="Salary slip not generated">
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>₹0.00</span>
                <InfoCircleOutlined 
                  style={{ 
                    color: themeConfig.text.mutedLight, 
                    fontSize: '14px'
                  }}
                />
              </div>
            </Tooltip>
          );
        }
        
        const netPayable = value || 0;
        // Calculate remaining balance after partial payments
        const partialPaidAmount = record.partialPaidAmount || 0;
        const remainingBalance = netPayable - partialPaidAmount;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div>
              <div style={{ fontWeight: 'bold' }}>₹{remainingBalance.toFixed(2)}</div>
              {partialPaidAmount > 0 && (
                <div style={{ fontSize: '11px', color: themeConfig.text.muted }}>
                  Original: ₹{netPayable.toFixed(2)}
                </div>
              )}
              {netPayable === 0 && (
                <div style={{ fontSize: '11px', color: themeConfig.text.mutedLight }}>
                  No amount payable
                </div>
              )}
            </div>
            <Tooltip title="View Salary Slip PDF">
              <InfoCircleOutlined 
                style={{ 
                  color: themeConfig.status.info, 
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
                onClick={() => handleViewSalarySlip(record)}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'Partial Paid',
      dataIndex: 'partialPaidAmount',
      key: 'partialPaidAmount',
      render: (value, record) => {
        if (!record.salarySlipGenerated) {
          return (
            <Tooltip title="Salary slip not generated">
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>₹0.00</span>
                <InfoCircleOutlined 
                  style={{ 
                    color: themeConfig.text.mutedLight, 
                    fontSize: '14px'
                  }}
                />
              </div>
            </Tooltip>
          );
        }
        
        const partialPaid = value || 0;
        
        if (partialPaid <= 0) {
          return (
            <Tooltip title="No partial payments made">
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span>₹0.00</span>
                <InfoCircleOutlined 
                  style={{ 
                    color: themeConfig.text.mutedLight, 
                    fontSize: '14px'
                  }}
                />
              </div>
            </Tooltip>
          );
        }
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>₹{partialPaid.toFixed(2)}</span>
            <Tooltip title="View partial payment details">
              <Button
                type="text"
                size="small"
                icon={<InfoCircleOutlined />}
                onClick={() => showPartialPayments(record)}
                style={{ padding: '0', minWidth: 'auto' }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        if (!record.salarySlipGenerated) return '-';
        
        // Check if fully paid
        const totalPartialPaid = record.partialPaidAmount || 0;
        const netPayable = record.netPayable || 0;
        const isFullyPaid = record.paymentStatus === 'PAID' || totalPartialPaid >= netPayable;
        
        if (isFullyPaid) {
          return (
            <div>
              <Tag color="green">Fully Paid</Tag>
              {(record.advanceCarriedForward || 0) > 0 && (
                <div style={{ fontSize: '11px', color: themeConfig.status.error, marginTop: '4px' }}>
                  Advance: -₹{record.advanceCarriedForward || 0}
                </div>
              )}
            </div>
          );
        }
        
        // Check if carried forward
        if (record.paymentStatus === 'CARRIED_FORWARD') {
          return (
            <div>
              <Tag color="default">Payment Disabled</Tag>
              <div style={{ fontSize: '11px', color: '#ff4d4f', marginTop: '4px' }}>
                ⚠️ Balance carried forward
              </div>
            </div>
          );
        }
        
        // Show Pay button
        return (
          <div>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handlePartialPayment(record)}
            >
              Pay
            </Button>
              {(record.advanceCarriedForward || 0) > 0 && (
                <div style={{ fontSize: '11px', color: themeConfig.status.error, marginTop: '4px' }}>
                  Advance: -₹{record.advanceCarriedForward || 0}
                </div>
              )}
          </div>
        );
      },
    },
  ];

  const handleMonthChange = (value) => {
    const [year, month] = value.split('-');
    setSelectedMonth(dayjs().year(parseInt(year)).month(parseInt(month) - 1).startOf('month'));
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handlePaymentAccountChange = (value) => {
    setSelectedPaymentAccount(value);
  };

  const clearAllFilters = () => {
    setSearchText('');
    setSelectedPaymentAccount('');
    setShowPendingOnly(false);
  };

  // Get unique payment accounts for filter dropdown
  const getUniquePaymentAccounts = () => {
    const accounts = [...new Set(paymentData.map(item => item.paymentAccount).filter(Boolean))];
    return accounts.sort().map(account => ({
      value: account,
      label: account
    }));
  };

  // Generate month options for the last 2 years and current year
  const generateMonthOptions = () => {
    const options = [];
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month();
    
    // Add last 2 years
    for (let year = currentYear - 2; year <= currentYear; year++) {
      for (let month = 0; month < 12; month++) {
        const date = dayjs().year(year).month(month).startOf('month');
        if (date.isSameOrBefore(dayjs().endOf('month'))) {
          options.push({
            value: `${year}-${month + 1}`,
            label: date.format('MMMM YYYY')
          });
        }
      }
    }
    return options.reverse(); // Most recent first
  };

  return (
    <div>
      {contextHolder}
      <Title level={2}>Salary Payment Management</Title>
      
      <div style={{ padding: '0px 0px' }}>
      <Row gutter={[24, 24]} justify="center">
        <Col xs={24} sm={24} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.small,
              textAlign: 'center',
            }}
          >
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              valueStyle={{ color: themeConfig.status.infoDark, fontWeight: 600 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={24} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.small,
              textAlign: 'center',
            }}
          >
            <Statistic
              title="Salary Slip Generated"
              value={stats.generatedSlips}
              valueStyle={{ color: themeConfig.status.successDark, fontWeight: 600 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={24} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.small,
              textAlign: 'center',
            }}
          >
            <Statistic
              title="Payment Pending"
              value={stats.pendingPayments}
              valueStyle={{ color: themeConfig.status.errorDark, fontWeight: 600 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={24} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.small,
              textAlign: 'center',
            }}
          >
            <Statistic
              title="Payment Carried Forward"
              value={stats.carriedForward}
              valueStyle={{ color: themeConfig.text.mutedDark, fontWeight: 600 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
      
       <div style={{ padding: '40px 0px' }}>
      <Row gutter={[24, 24]} justify="center">
        <Col xs={24} sm={24} md={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.small,
              textAlign: 'center',
            }}
          >
            <Statistic
              title={
                <Space>
                  {showGrossSalary ? 'Gross Salary' : 'Total Salary'}
                  <Switch
                    checked={showGrossSalary}
                    onChange={setShowGrossSalary}
                    checkedChildren="Gross"
                    unCheckedChildren="Total"
                    size="small"
                  />
                  <Tooltip title="View 6-month trend">
                    <Button 
                      type="text" 
                      icon={<LineChartOutlined />} 
                      onClick={handleShowGraph}
                      size="small"
                    />
                  </Tooltip>
                </Space>
              }
              value={showGrossSalary ? stats.totalGrossSalary : stats.totalSalary}
              prefix={<DollarOutlined />}
              precision={2}
              formatter={(value) => `₹${value.toLocaleString()}`}
              valueStyle={{ color: themeConfig.status.info, fontWeight: 600 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.small,
              textAlign: 'center',
            }}
          >
            <Statistic
              title="Salary Paid"
              value={stats.totalPaid}
              prefix={<DollarOutlined />}
              precision={2}
              formatter={(value) => `₹${value.toLocaleString()}`}
              valueStyle={{ color: themeConfig.status.successDark, fontWeight: 600 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.small,
              textAlign: 'center',
            }}
          >
            <Statistic
              title="Salary Pending"
              value={stats.paymentPending}
              prefix={<DollarOutlined />}
              precision={2}
              formatter={(value) => `₹${value.toLocaleString()}`}
              valueStyle={{ color: themeConfig.status.errorDark, fontWeight: 600 }}
            />
          </Card>
        </Col>
      </Row>
    </div>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Select
              placeholder="Select Month"
              style={{ width: 200 }}
              value={`${selectedMonth.year()}-${selectedMonth.month() + 1}`}
              onChange={handleMonthChange}
              options={generateMonthOptions()}
            />
            <Select
              placeholder="All Payment Accounts"
              style={{ width: 200 }}
              value={selectedPaymentAccount}
              onChange={handlePaymentAccountChange}
              allowClear
              options={getUniquePaymentAccounts()}
            />
            <Search
              placeholder="Search by name, job, or mobile number"
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
            />
            <Button onClick={fetchPaymentData} loading={loading}>
              Refresh
            </Button>
            <Button onClick={clearAllFilters}>
              Clear Filters
            </Button>
            <Button 
              icon={<PrinterOutlined />} 
              onClick={handlePrint}
              disabled={!paymentData || paymentData.length === 0}
            >
              Print Report
            </Button>
            <Checkbox 
              checked={showPendingOnly}
              onChange={(e) => setShowPendingOnly(e.target.checked)}
            >
              Show Pending
            </Checkbox>
          </Space>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="mobileNumber"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            scroll={{ x: 1200 }}
            style={{
              '& .ant-table-thead > tr > th': {
                backgroundColor: themeConfig.status.errorBg,
                color: 'var(--tertiary-text)',
                fontWeight: '600',
              },
              '& .ant-table-tbody > tr:nth-child(even) > td': {
                backgroundColor: themeConfig.background.tableRowEven,
              },
              '& .ant-table-tbody > tr:nth-child(odd) > td': {
                backgroundColor: themeConfig.background.tableRowOdd,
              },
              '& .ant-table-tbody > tr:hover > td': {
                backgroundColor: themeConfig.background.container,
              }
            }}
          />
        )}
      </Card>

      {/* Partial Payment Modal */}
      <Modal
        title="Record Payment"
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        onOk={handlePaymentSubmit}
        confirmLoading={paymentLoading}
        destroyOnClose
        zIndex={1500}
      >
        {selectedEmployee && (
          <div>
            <p><strong>Employee:</strong> {selectedEmployee.fullName}</p>
            <p><strong>Payment Account:</strong> {selectedEmployee.paymentAccount || 'Not Set'}</p>
            <p><strong>Net Payable:</strong> ₹{selectedEmployee.netPayable?.toFixed(2)}</p>
            <p><strong>Already Paid:</strong> ₹{selectedEmployee.partialPaidAmount?.toFixed(2) || '0.00'}</p>
            <p><strong>Remaining Balance:</strong> ₹{((selectedEmployee.netPayable || 0) - (selectedEmployee.partialPaidAmount || 0)).toFixed(2)}</p>
            
            <div style={{ marginTop: '16px' }}>
              <label><strong>Payment Amount:</strong></label>
              <InputNumber
                style={{ width: '100%', marginTop: '8px' }}
                prefix="₹"
                value={paymentAmount}
                onChange={setPaymentAmount}
                min={0.01}
                max={((selectedEmployee.netPayable || 0) - (selectedEmployee.partialPaidAmount || 0))}
                precision={2}
                placeholder="Enter payment amount"
              />
            </div>
            
            <div style={{ marginTop: '16px' }}>
              <label><strong>Payment Narration:</strong></label>
              <Input.TextArea
                style={{ width: '100%', marginTop: '8px' }}
                value={paymentNarration}
                onChange={(e) => setPaymentNarration(e.target.value)}
                placeholder="Enter brief description of the payment (e.g., First installment, Advance payment, etc.)"
                rows={3}
                maxLength={200}
                showCount
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Partial Payments History Modal */}
      <Modal
        title="Partial Payment History"
        open={partialPaymentsModalVisible}
        onCancel={() => setPartialPaymentsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPartialPaymentsModalVisible(false)}>
            Close
          </Button>
        ]}
        destroyOnClose
        zIndex={1000}
      >
        {selectedEmployeePartialPayments.length > 0 ? (
          <div>
            <p><strong>Employee:</strong> {selectedEmployee?.fullName}</p>
            <p><strong>Payment Account:</strong> {selectedEmployee?.paymentAccount || 'Not Set'}</p>
            <p><strong>Total Partial Paid:</strong> ₹{selectedEmployeePartialPayments.reduce((sum, payment) => sum + payment.amount, 0).toFixed(2)}</p>
            
            <Table
              dataSource={selectedEmployeePartialPayments.map((payment, index) => ({ ...payment, key: index }))}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'paymentDate',
                  key: 'paymentDate',
                  render: (date) => dayjs(date).format('DD MMM YYYY'),
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  key: 'amount',
                  render: (amount) => `₹${amount.toFixed(2)}`,
                },
                {
                  title: 'Narration',
                  dataIndex: 'narration',
                  key: 'narration',
                  render: (narration) => narration || <span style={{ color: themeConfig.text.mutedVeryLight }}>No description</span>,
                  width: 200,
                },
                {
                  title: 'Remaining Balance',
                  dataIndex: 'remainingBalance',
                  key: 'remainingBalance',
                  render: (balance) => `₹${balance.toFixed(2)}`,
                },
                {
                  title: 'Actions',
                  key: 'actions',
                  render: (_, record, index) => (
                    <Space>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditPayment(record, selectedEmployee, index)}
                        style={{ color: themeConfig.status.info }}
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Are you sure you want to delete this payment?"
                        description="This action cannot be undone and will affect the salary slip balance."
                        onConfirm={() => handleDeletePayment(record, selectedEmployee, index)}
                        okText="Yes, Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          style={{ color: themeConfig.status.error }}
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
              pagination={false}
              size="small"
              style={{
                '& .ant-table-thead > tr > th': {
                  backgroundColor: themeConfig.status.errorBg,
                  color: themeConfig.colors.tertiaryText,
                  fontWeight: '600',
                },
                '& .ant-table-tbody > tr:nth-child(even) > td': {
                  backgroundColor: themeConfig.background.tableRowEven,
                },
                '& .ant-table-tbody > tr:nth-child(odd) > td': {
                  backgroundColor: themeConfig.background.tableRowOdd,
                },
                '& .ant-table-tbody > tr:hover > td': {
                  backgroundColor: themeConfig.background.container,
                }
              }}
            />
          </div>
        ) : (
          <p>No partial payment records found.</p>
        )}
      </Modal>

      {/* Print Modal */}
      <Modal
        title="Print Salary Payment Report"
        visible={isPrintModalVisible}
        onCancel={() => setIsPrintModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsPrintModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
            Download CSV
          </Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintContent}>
            Print
          </Button>
        ]}
        width={1200}
        destroyOnClose
      >
        <div id="printable-salary-payment-content">
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <h2>Monthly Salary Payment Report</h2>
            <p><strong>Month:</strong> {selectedMonth.format('MMMM YYYY')}</p>
            <p><strong>Generated on:</strong> {new Date().toLocaleDateString()}</p>
          </div>
          
          <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${themeConfig.border.light}` }}>
            <thead>
              <tr style={{ backgroundColor: themeConfig.background.tableHeader }}>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Employee Name</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Payment Account</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Salary Slip Status</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Payment Status</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Net Payable</th>
                <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Partial Paid</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((record, index) => (
                <tr key={record.mobileNumber} style={{ backgroundColor: index % 2 === 0 ? themeConfig.background.tableRowOdd : themeConfig.background.tableRowEven }}>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.fullName || 'N/A'} ({record.job || 'N/A'})</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.paymentAccount || 'Not Set'}</td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>
                    <span style={{ 
                      color: record.salarySlipGenerated ? themeConfig.status.success : themeConfig.status.warningLight
                    }}>
                      {record.salarySlipGenerated ? 'Generated' : 'Not Generated'}
                    </span>
                  </td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>
                    <span style={{ 
                      color: record.paymentStatus === 'PAID' ? themeConfig.status.success : 
                             record.paymentStatus === 'PARTIAL_PAID' ? themeConfig.status.warningLight : 
                             record.paymentStatus === 'PENDING' ? themeConfig.status.error : 
                             record.paymentStatus === 'AUTO' ? themeConfig.status.info : 
                             record.paymentStatus === 'ADVANCE' ? themeConfig.status.success : 
                             themeConfig.text.mutedLight
                    }}>
                      {record.salarySlipGenerated ? (() => {
                        switch (record.paymentStatus) {
                          case 'PAID': return 'Fully Paid';
                          case 'PENDING': return 'Pending';
                          case 'PARTIAL_PAID': return 'Partial Paid';
                          case 'CARRIED_FORWARD': return 'Carried Forward';
                          case 'AUTO': return 'Auto Generated';
                          case 'ADVANCE': return 'Adjusted Against Advance';
                          default: return record.paymentStatus || 'N/A';
                        }
                      })() : 'N/A'}
                    </span>
                  </td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>
                    {record.salarySlipGenerated ? `₹${(record.netPayable || 0).toFixed(2)}` : 'N/A'}
                  </td>
                  <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>
                    {record.salarySlipGenerated && record.partialPaidAmount ? `₹${record.partialPaidAmount.toFixed(2)}` : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <p><strong>Total Users:</strong> {stats.totalUsers}</p>
                <p><strong>Salary Slips Generated:</strong> {stats.generatedSlips}</p>
                <p><strong>Pending Payments:</strong> {stats.pendingPayments + stats.partialPayments}</p>
              </div>
              <div>
                <p><strong>Fully Paid:</strong> {stats.paidSlips}</p>
                <p><strong>Total Salary:</strong> ₹{stats.totalSalary.toFixed(2)}</p>
                <p><strong>Salary Paid:</strong> ₹{stats.totalPaid.toFixed(2)}</p>
                <p><strong>Salary Pending:</strong> ₹{stats.paymentPending.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* PDF Salary Slip Modal */}
      <Modal
        title={`Salary Slip - ${selectedEmployeeForPdf?.fullName || ''}`}
        open={pdfModalVisible}
        onCancel={() => setPdfModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPdfModalVisible(false)}>
            Close
          </Button>
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        {pdfUrl && (
          <div style={{ height: '80vh' }}>
            <iframe
              src={pdfUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="Salary Slip PDF"
            />
          </div>
        )}
      </Modal>

      {/* Edit Payment Modal */}
      <Modal
        title="Edit Payment"
        open={editPaymentModalVisible}
        onCancel={() => {
          setEditPaymentModalVisible(false);
          setEditingPayment(null);
          setEditPaymentAmount(0);
          setEditPaymentNarration('');
        }}
        onOk={handleEditPaymentSubmit}
        okText="Update Payment"
        cancelText="Cancel"
        okButtonProps={{ disabled: editPaymentAmount <= 0 }}
        zIndex={2000}
        maskClosable={false}
      >
        <div style={{ marginBottom: 16 }}>
          <p><strong>Employee:</strong> {editingPayment?.employee?.fullName}</p>
          <p><strong>Original Amount:</strong> ₹{editingPayment?.amount?.toFixed(2)}</p>
        </div>
        
        <Form layout="vertical">
          <Form.Item label="New Amount" required>
            <InputNumber
              style={{ width: '100%' }}
              value={editPaymentAmount}
              onChange={setEditPaymentAmount}
              min={0.01}
              step={0.01}
              precision={2}
              placeholder="Enter new payment amount"
              prefix="₹"
            />
          </Form.Item>
          
          <Form.Item label="Narration">
            <Input
              value={editPaymentNarration}
              onChange={(e) => setEditPaymentNarration(e.target.value)}
              placeholder="Enter payment description (optional)"
              maxLength={100}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Salary Trend Graph Modal */}
      <Modal
        title="Total Salary Trend - Past 6 Months"
        open={graphModalVisible}
        onCancel={() => setGraphModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setGraphModalVisible(false)}>
            Close
          </Button>
        ]}
        width={900}
        style={{ top: 20 }}
      >
        <Spin spinning={graphLoading}>
          {graphData.length > 0 ? (
            <div style={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={graphData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="month" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                  />
                  <RechartsTooltip 
                    formatter={(value, name) => {
                      if (name === 'totalSalary') {
                        return [`₹${value.toLocaleString()}`, 'Total Salary'];
                      }
                      return [value, 'Employees'];
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="totalSalary" 
                    stroke={themeConfig.status.info} 
                    strokeWidth={2}
                    name="Total Salary"
                    dot={{ r: 5 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              No data available
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default SalaryPayment; 